import { defineStore } from 'pinia';
import http from '@/services/http';
import { useAuthStore } from '@/stores/auth';

export const useCompaniesStore = defineStore('companies', {
  state: () => ({
    companies: [],
    loading: false,
    error: null,
    lastApiResponse: null
  }),
  
  getters: {
    getAllCompanies: (state) => state.companies,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    getCompanyById: (state) => (id) => state.companies.find(company => company.id === id),
    getLastApiResponse: (state) => state.lastApiResponse
  },
  
  actions: {
    async fetchCompanies() {
      this.loading = true;
      this.error = null;
      this.lastApiResponse = null;
      
      const authStore = useAuthStore();
      const token = authStore.token;
      const isSuperAdmin = authStore.isSuperAdmin;
      
      console.log('Starte Firmen-Abruf, Auth-Status:', { 
        token: !!token, 
        isSuperAdmin,
        user: authStore.user
      });
      
      try {
        // Versuche verschiedene mögliche API-Endpunkte
        let response;
        let success = false;
        let apiEndpoints = [];
        
        // Bestimme die richtigen Endpunkte basierend auf dem Benutzertyp
        if (isSuperAdmin) {
          apiEndpoints = ['/api/admin/companies', '/api/companies'];
        } else {
          apiEndpoints = ['/api/company', '/api/companies'];
        }
        
        // Versuche jeden Endpunkt
        for (const endpoint of apiEndpoints) {
          try {
            console.log(`Versuche API-Endpunkt: ${endpoint}`);
            response = await http.get(endpoint);
            console.log(`API-Antwort von ${endpoint}:`, response);
            success = true;
            this.lastApiResponse = {
              endpoint,
              status: response.status,
              data: response.data
            };
            break; // Wenn erfolgreich, die Schleife beenden
          } catch (err) {
            console.warn(`Fehler beim Abrufen von ${endpoint}:`, err);
          }
        }
        
        if (!success) {
          throw new Error('Alle API-Endpunkte fehlgeschlagen');
        }
        
        // Datenverarbeitung
        console.log('Verarbeite API-Antwort:', response.data);
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            this.companies = response.data;
          } else if (response.data.companies && Array.isArray(response.data.companies)) {
            this.companies = response.data.companies;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            this.companies = response.data.data;
          } else if (response.data.items && Array.isArray(response.data.items)) {
            this.companies = response.data.items;
          } else if (typeof response.data === 'object' && response.data !== null) {
            // Es könnte eine einzelne Firma sein
            const singleCompany = response.data;
            if (singleCompany.id) {
              this.companies = [singleCompany];
            } else {
              // Versuche, alle Properties zu prüfen, ob sie ein Array sind
              let foundArray = false;
              for (const key in singleCompany) {
                if (Array.isArray(singleCompany[key])) {
                  console.log(`Fand Array unter Property '${key}'`);
                  this.companies = singleCompany[key];
                  foundArray = true;
                  break;
                }
              }
              
              if (!foundArray) {
                console.warn('Keine Firmendaten gefunden in:', response.data);
                this.companies = [];
              }
            }
          } else {
            console.warn('Unbekannte Datenstruktur:', response.data);
            this.companies = [];
          }
        } else {
          console.warn('Keine Daten in der Antwort');
          this.companies = [];
        }
        
        console.log('Verarbeitete Firmen:', this.companies);
        return this.companies;
      } catch (error) {
        console.error('FEHLER: fetchCompanies -', error.response ? error.response.data.message : error.message);
        this.error = error.response ? error.response.data.message : error.message;
        this.companies = [];
        return [];
      } finally {
        this.loading = false;
      }
    },
    
    async createCompany(companyData) {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await http.post('/api/companies', companyData);
        // Neue Firma zur Liste hinzufügen
        this.companies.push(response.data);
        return response.data;
      } catch (error) {
        console.error('FEHLER: createCompany -', error.response ? error.response.data.message : error.message);
        this.error = error.response ? error.response.data.message : error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async updateCompany(companyData) {
      this.loading = true;
      this.error = null;
      
      try {
        const { id, ...data } = companyData;
        const response = await http.put(`/api/companies/${id}`, data);
        
        // Firma in der Liste aktualisieren
        const index = this.companies.findIndex(c => c.id === id);
        if (index !== -1) {
          this.companies[index] = response.data;
        }
        
        return response.data;
      } catch (error) {
        console.error('FEHLER: updateCompany -', error.response ? error.response.data.message : error.message);
        this.error = error.response ? error.response.data.message : error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async deleteCompany(id) {
      this.loading = true;
      this.error = null;
      
      try {
        await http.delete(`/api/companies/${id}`);
        
        // Firma aus der Liste entfernen
        this.companies = this.companies.filter(c => c.id !== id);
        
        return true;
      } catch (error) {
        console.error('FEHLER: deleteCompany -', error.response ? error.response.data.message : error.message);
        this.error = error.response ? error.response.data.message : error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async getCompany(id) {
      try {
        const response = await http.get(`/api/companies/${id}`);
        
        // Firma im Cache aktualisieren, falls sie bereits existiert
        const index = this.companies.findIndex(c => c.id === id);
        if (index !== -1) {
          this.companies[index] = response.data;
        } else {
          this.companies.push(response.data);
        }
        
        return response.data;
      } catch (error) {
        console.error('FEHLER: getCompany -', error.response ? error.response.data.message : error.message);
        throw error;
      }
    }
  }
}); 